import { ChangeDetectionStrategy, Component, computed, inject, Signal, Type } from '@angular/core';
import { NgComponentOutlet, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonTypeEnum, PButtonModule } from '@panaya/ui/p-button';
import { AiChatInternalPromptService } from '@panaya/ai-chat/domain';
import { CanvasAction, CanvasRequestContext } from '@panaya/ai-chat/shared';
import { initializeAiChatTranslations } from '@panaya/ai-chat/utils';
import { CanvasFrameStore } from '../canvas-frame.store';
import { CanvasDisplayRegistry } from '../canvas-display.registry';
import { CANVAS_CONTENT_CONTRIBUTOR, CanvasContentContributor, NO_CONTENT_CONTRIBUTOR } from '../canvas-content-contributor';
import { CanvasBusyPillComponent } from '../canvas-busy-pill/canvas-busy-pill.component';
import { CanvasEmptyStateComponent } from '../canvas-empty-state/canvas-empty-state.component';

/**
 * Frame-level states. `thinking`: the agent's tool opened the canvas and nothing has arrived yet. Progress inside
 * the content belongs to the display.
 */
export type FrameState = 'thinking' | 'awaiting' | 'creating' | 'error' | 'content';
export type FooterMode = 'busy' | 'actions' | 'none';

/**
 * The canvas frame: toolbar, content slot and footer. The content slot renders the component registered for
 * `display_type`, and the content tells the frame what it needs through `CANVAS_CONTENT_CONTRIBUTOR`.
 */
@Component({
    standalone: true,
    selector: 'p-ai-chat-canvas-frame',
    imports: [
        NgIf,
        NgFor,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        NgComponentOutlet,
        TranslateModule,
        MatTooltipModule,
        PButtonModule,
        CanvasBusyPillComponent,
        CanvasEmptyStateComponent
    ],
    templateUrl: './canvas-frame.component.html',
    styleUrls: ['./canvas-frame.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CanvasFrameComponent {
    protected readonly frameStore = inject(CanvasFrameStore);
    private readonly registry = inject(CanvasDisplayRegistry);
    private readonly contributor: CanvasContentContributor =
        inject(CANVAS_CONTENT_CONTRIBUTOR, { optional: true }) ?? NO_CONTENT_CONTRIBUTOR;
    private readonly internalPrompt = inject(AiChatInternalPromptService);

    protected readonly buttonType = ButtonTypeEnum;
    protected displayComponent!: Signal<Type<unknown> | null>;
    protected displayInputs!: Signal<{ params: Record<string, unknown> }>;
    state!: Signal<FrameState>;
    footerMode!: Signal<FooterMode>;
    /** The title appears once the agent has something to show; before that the toolbar strip is empty. */
    hasToolbarContent!: Signal<boolean>;
    /** The error text the footer shows while content is shown. */
    footerError!: Signal<string | null>;
    /** The content's own header text, such as the draft's title, once it has one. */
    protected title!: Signal<string | null>;
    /** Shown until the content has a title: the registration's key, else the library's default. */
    protected titleKey!: Signal<string>;
    /** The content's locale key for the disabled primary action's tooltip; empty, so no tooltip, while it is enabled or the content gives none. */
    protected primaryActionHintKey!: Signal<string>;
    /** The empty canvas's title while the agent generates: the registration's key, else the library's generic one. */
    protected generatingKey!: Signal<string>;
    /** The busy pill's text: creating after Create, else preparing while the draft fills. */
    protected busyKey!: Signal<string>;

    constructor() {
        initializeAiChatTranslations();
        this.initSignals();
    }

    isActionEnabled(primary: boolean): boolean {
        if (this.frameStore.status() === 'creating') {
            return false;
        }
        return primary ? this.contributor.primaryActionEnabled() : true;
    }

    /** Sends the action's sentence as a hidden prompt with `canvas.action` set. */
    onAction(action: CanvasAction): void {
        const flow = this.frameStore.flow();
        if (!flow) {
            return;
        }
        const canvas: CanvasRequestContext = { flow, ...this.contributor.requestContext(), action: action.id };
        this.internalPrompt.triggerInternalPrompt({
            source: 'internal',
            message: action.prompt,
            hidden: true,
            showLoading: true,
            contextExtras: { canvas }
        });
    }

    protected trackAction(_index: number, action: CanvasAction): string {
        return action.id;
    }

    /** Separate from the field initialisers so a spec can build the signals on a bare prototype instance. */
    private initSignals(): void {
        this.displayComponent = computed(() => {
            const displayType = this.frameStore.displayType();
            return displayType ? this.registry.resolve(displayType) : null;
        });
        this.displayInputs = computed(() => ({ params: this.frameStore.params() }));
        this.title = computed(() => this.contributor.title());
        this.titleKey = computed(() => this.frameStore.titleKey() ?? 'aichat.canvas.default_title');
        this.primaryActionHintKey = computed(() => (this.isActionEnabled(true) ? '' : this.contributor.primaryActionHint?.() ?? ''));
        this.generatingKey = computed(() => this.frameStore.progressKey('generating', 'aichat.canvas.thinking.title'));
        this.busyKey = computed(() =>
            this.frameStore.status() === 'creating'
                ? this.frameStore.progressKey('creating', 'aichat.canvas.busy.creating')
                : this.frameStore.progressKey('preparing', 'aichat.canvas.busy.preparing')
        );
        this.state = computed((): FrameState => {
            const status = this.frameStore.status();

            const hasContent = this.contributor.hasContent();

            // The agent's question shows over the empty canvas; with a draft, the draft stays in view.
            if (status === 'awaiting') {
                return hasContent ? 'content' : 'awaiting';
            }
            if (status === 'creating') {
                return 'creating';
            }
            if (status === null && !hasContent) {
                return 'thinking';
            }

            if (this.displayComponent() === null) {
                return 'error';
            }
            if (status === 'error' && !hasContent) {
                return 'error';
            }
            return 'content';
        });
        this.hasToolbarContent = computed(() => this.state() !== 'awaiting' && this.state() !== 'thinking');
        this.footerMode = computed((): FooterMode => {
            const state = this.state();

            // Nothing to act on yet: no footer while the agent generates or waits for an answer.
            if (state === 'thinking' || this.frameStore.status() === 'awaiting') {
                return 'none';
            }

            if (state === 'creating' || this.contributor.busy()) {
                return 'busy';
            }
            if (state === 'error') {
                return 'none';
            }
            return 'actions';
        });
        // Before any content the error state shows the message; the footer carries it only under content. The error
        // comes from canvas_status or from the content itself, such as a draft whose status is error.
        this.footerError = computed(() => {
            if (this.state() !== 'content') {
                return null;
            }

            return this.frameStore.status() === 'error' ? this.frameStore.statusMessage() : this.contributor.errorMessage?.() ?? null;
        });
    }
}
